// Internal staff messaging service — channels, messages, and generic Task
// assignment. Mirrors clinicComplianceService.js's fetch conventions.
// Order assignment reuses clinicComplianceService.assignOrderItem instead of
// duplicating it here — see backend/api/clinic_orders.py.
//
// Who is asking (backend/api/messaging.py): every call sends the signed-in
// user's Supabase access token (the token source authedFetch uses), and the
// server takes the caller from it. No request body names the signed-in user:
// the parameters below that name them (senderId, userId, actorUserId,
// inviterUserId, actorId, a task's assigned_by) are kept so that callers need
// not change, and are never sent. The read filters user_id, involving and
// assigned_to still go in the query string, and the server refuses one that
// names anyone else. A channel's live feed signs in with its first message
// (authenticateSocket), never with its URL.

import { getBackendURL } from "../components/env";
import { getAccessTokenSafe, SESSION_EXPIRED } from "./orderPaperwork";
import { currentToken, refreshedToken, staffRequest } from "./staffRequest";

// The token on every call, one refresh-and-retry after a 401 (staffRequest).
const messagingRequest = (path, { method = "GET", body, signal } = {}) =>
  staffRequest(`${getBackendURL()}/api/internal-messaging${path}`, {
    method, body, signal, failure: "Internal messaging API error",
  });

const messagingFetch = (path, { signal } = {}) => messagingRequest(path, { signal });

const messagingPost = (path, body, { signal } = {}) => messagingRequest(path, { method: "POST", body, signal });

export const fetchChannels = (userId, opts = {}) =>
  messagingFetch(`/channels/?user_id=${encodeURIComponent(userId)}`, opts);

export const createChannel = (name, description, channelType, _actorUserId, opts = {}) =>
  messagingPost("/channels/create/", { name, description, channel_type: channelType }, opts);

export const deleteChannel = (channelId, _actorUserId, opts = {}) =>
  messagingPost("/channels/delete/", { channel_id: channelId }, opts);

export const joinChannel = (channelId, _userId, opts = {}) =>
  messagingPost("/channels/join/", { channel_id: channelId }, opts);

export const inviteToChannel = (channelId, _inviterUserId, inviteeUserId, opts = {}) =>
  messagingPost("/channels/invite/", { channel_id: channelId, invitee_user_id: inviteeUserId }, opts);

export const leaveChannel = (channelId, _userId, opts = {}) =>
  messagingPost("/channels/leave/", { channel_id: channelId }, opts);

export const getOrCreateDm = (_userId, otherUserId, opts = {}) =>
  messagingPost("/dm/", { other_user_id: otherUserId }, opts);

// { messages, read_by, peer_read_at } — read_by: every other member with
// when they last read the thread ([{ user_id, name, read_at }]), for the
// "Seen by ..." receipts.
export const fetchMessages = (channelId, userId, opts = {}) =>
  messagingFetch(
    `/messages/?channel_id=${encodeURIComponent(channelId)}${userId ? `&user_id=${encodeURIComponent(userId)}` : ""}`,
    opts,
  );

export const sendMessage = (channelId, _senderId, body, opts = {}) =>
  messagingPost(
    "/messages/send/",
    {
      channel_id: channelId,
      body,
      ...(opts.format === "html" ? { body_format: "html" } : {}),
      // Only when there are files: text-only messages keep working on a
      // database without the attachments column.
      ...(opts.attachments && opts.attachments.length ? { attachments: opts.attachments } : {}),
      // A post in an agent's thread: the same key sent again is answered with
      // the post it made (MessagingModal makes it).
      ...(opts.clientKey ? { client_key: opts.clientKey } : {}),
      // Who the composer's @list named ({user_ids, here, channel}); only when
      // there is someone. @here / @channel typed in the text count server-side anyway.
      ...(opts.mentions ? { mentions: opts.mentions } : {}),
    },
    opts,
  );

// One-time upload links for a message's files (private internal-messaging bucket).
export const requestAttachmentUploads = (channelId, _senderId, files, opts = {}) =>
  messagingPost("/attachments/upload-urls/", { channel_id: channelId, files }, opts);

// A fresh 1-hour link for an attachment whose original link has expired. A
// POST, so the path (which holds the file's name) never goes in a URL.
export const fetchAttachmentUrl = (channelId, path, opts = {}) =>
  messagingPost("/attachments/url/", { channel_id: channelId, path }, opts);

export const markChannelRead = (channelId, _userId, opts = {}) =>
  messagingPost("/channels/mark-read/", { channel_id: channelId }, opts);

// { counts, mentions, settings, total, mention_total } (docs/specs/messaging-notifications-spec.md).
// Before the 2026-11-03 migration mentions / settings are {} and mention_total 0.
export const fetchUnreadCounts = (userId, opts = {}) =>
  messagingFetch(`/unread/?user_id=${encodeURIComponent(userId)}`, opts);

// The caller's notification levels: { desktop_level, mobile_level }, each 'all' | 'mentions' | 'none'.
export const fetchNotificationPrefs = (opts = {}) => messagingFetch("/notifications/prefs/", opts);

export const saveNotificationPrefs = (prefs, opts = {}) => messagingPost("/notifications/prefs/", prefs, opts);

// One channel's override for the caller: { notify_level?: 'default'|'all'|'mentions'|'none', muted?: bool }
// → { notify_level, muted } as saved.
export const setChannelNotify = (channelId, settings, opts = {}) =>
  messagingPost("/channels/notify/", { channel_id: channelId, ...settings }, opts);

// { presence: { user_id: "online" | "idle" | "offline" } } — drives the DM list dots.
export const fetchPresence = (userIds, opts = {}) =>
  messagingFetch(`/presence/?user_ids=${encodeURIComponent(userIds.join(","))}`, opts);

// Scoped to the caller's schema group (every schema sharing its
// schema_settings "Group", or just the schema when it's ungrouped) — for DMs,
// invites and assigning work, unlike clinicComplianceService.searchUsers
// which searches everyone.
export const searchColleagues = (query, schema, opts = {}) =>
  messagingFetch(`/users/search/?q=${encodeURIComponent(query)}&schema=${encodeURIComponent(schema || "public")}`, opts);

export const fetchTasks = (assignedTo, opts = {}) =>
  messagingFetch(assignedTo ? `/tasks/?assigned_to=${encodeURIComponent(assignedTo)}` : "/tasks/", opts);

// Tasks assigned to OR by this user — the Messages modal's Tasks tab.
export const fetchTasksInvolving = (userId, opts = {}) =>
  messagingFetch(`/tasks/?involving=${encodeURIComponent(userId)}`, opts);

// Tasks assigned to AND by members of one of my channels — the Tasks tab's
// channel picker. Resolves to { tasks, member_ids } (403 if I'm not in it).
export const fetchTasksInChannel = (channelId, userId, opts = {}) =>
  messagingFetch(`/tasks/?channel=${encodeURIComponent(channelId)}&user_id=${encodeURIComponent(userId)}`, opts);

// Every task made from a given kind of source (e.g. "memory_issue"), each with
// source_ref naming the source it points back at.
// `refs` narrows it to the tasks made from those sources (a chart's signed
// orders, which are only ever listed that way).
export const fetchTasksBySource = (sourceKind, { refs, ...opts } = {}) => {
  const params = new URLSearchParams({ source_kind: sourceKind });
  (refs || []).forEach((ref) => params.append("source_ref", ref));
  return messagingFetch(`/tasks/?${params.toString()}`, opts);
};

// Every task made from these patients' charts in `schema` (any visit), whoever
// assigned it or holds it — the Schedules row actions' Tasks chip. Resolves to
// { tasks }, each with the people on it named.
export const fetchTasksForPatients = (schema, patients, opts = {}) =>
  messagingPost("/tasks/by-patient/", { schema, patients }, opts);

// The server records the caller as the one who assigned it.
export const createTask = (task, opts = {}) => {
  const { assigned_by: _assignedBy, ...fields } = task || {};
  return messagingPost("/tasks/create/", fields, opts);
};

export const updateTaskStatus = (taskId, status, opts = {}) =>
  messagingPost("/tasks/status/", { task_id: taskId, status }, opts);

export const fetchTask = (taskId, opts = {}) => messagingFetch(`/tasks/${encodeURIComponent(taskId)}/`, opts);

export const transitionTask = (taskId, action, _actorId, opts = {}) =>
  messagingPost(
    "/tasks/transition/",
    {
      task_id: taskId, action,
      ...(opts.message ? { message: opts.message } : {}),
      ...(opts.to ? { to: opts.to } : {}),
    },
    opts,
  );

// The person who assigned a task changes its title, description, due date
// (YYYY-MM-DD; null clears it) or priority. Resolves to { task } as saved.
export const editTask = (taskId, _actorId, { title, description, dueDate, priority }, opts = {}) =>
  messagingPost(
    "/tasks/edit/",
    { task_id: taskId, title, description, due_date: dueDate, priority },
    opts,
  );

// Builds the ws(s):// URL for a channel's live message feed from the same
// backend origin the REST calls use (getBackendURL() returns an http(s) URL).
// It carries no token: URLs end up in logs.
export function messagingSocketUrl(channelId) {
  const base = getBackendURL().replace(/^http/, "ws");
  return `${base}/ws/messaging/${channelId}`;
}

// The signed-in person's own notification feed (one per tab, utils/messagingNotifications.js).
// Signs in with authenticateSocket, like a channel's feed.
export function notificationsSocketUrl() {
  const base = getBackendURL().replace(/^http/, "ws");
  return `${base}/ws/notifications/`;
}

const SOCKET_OPEN = 1; // WebSocket.OPEN

// The live feed's first message, sent once the socket opens: the server adds it
// to the channel only after it has checked this token and that the user is in
// the channel (backend/api/consumers.py MessagingConsumer), and closes it
// otherwise. Resolves true once sent, false when the socket closed meanwhile.
// Without a session it closes the socket rather than leave it waiting.
// `refresh` asks Supabase for a new token first: the server closed the last
// socket because its token was refused or had expired.
export async function authenticateSocket(socket, { refresh = false } = {}) {
  const token = refresh ? (await refreshedToken()) || (await getAccessTokenSafe()) : await currentToken();
  if (!token) {
    socket.close();
    throw new Error(SESSION_EXPIRED);
  }
  if (socket.readyState !== SOCKET_OPEN) return false;
  socket.send(JSON.stringify({ type: "auth", token }));
  return true;
}
